/**
 * The run controls for the Optimization tab — window, focus, and Run.
 *
 * Rendered ABOVE the conversation, and present the whole time the tab is open.
 *
 * That placement is the point. These controls used to live inside
 * OptimizationRunGate, which the sidebar renders as its `fallbackMessage` — i.e.
 * only when there is NO session. So they appeared exactly once, before the first
 * run, and vanished the moment a run loaded: a campaign with any completed run
 * opened straight into the conversation with no date picker, no focus box, and no
 * way to run again. (The agent's own prompt told users to "use the Re-run control
 * in the panel", which by then did not exist.)
 *
 * Collapsed by default once a run is loaded, because at that point the
 * conversation is the thing the user came for and this is a control they may
 * never touch. Expanded when there is nothing to read yet, because then it is the
 * only thing to do.
 */

import { useState } from 'react';
import { optimizeAnalysesEnabledFor } from '../../../utils/optimizeCampaignRun';
import { OptimizeWindowFields } from '../../OptimizeWindowFields';
import {
  buildOptimizeDateParams,
  describeOptimizeWindow,
  optimizeAcceptsDateWindow,
  DEFAULT_OPTIMIZE_PRESET,
} from '../../../utils/optimizeDateRange';

export function OptimizationRunBar({
  destination,
  // Whether a run is already loaded — decides the wording and whether this
  // starts collapsed.
  hasRun = false,
  // A run is in flight; the button is the wrong affordance until it settles.
  running = false,
  disabled = false,
  disabledReason = null,
  // (customPrompt, dateParams) => void
  onRun,
}) {
  const fullAnalysis = optimizeAnalysesEnabledFor(destination);
  const [open, setOpen] = useState(!hasRun);
  const [opts, setOpts] = useState({ preset: DEFAULT_OPTIMIZE_PRESET, customPrompt: '' });

  const fire = () => {
    if (disabled || running) return;
    onRun?.(
      (opts.customPrompt || '').trim() || null,
      buildOptimizeDateParams(opts, destination),
    );
  };

  // Only worth summarising where a window exists to summarise. On the platforms
  // that read none, saying "analysing all available data" would be describing a
  // setting the run does not have.
  const summary = optimizeAcceptsDateWindow(destination)
    ? describeOptimizeWindow(opts, destination)
    : null;

  return (
    <div className={`clw-opt-runbar${open ? ' open' : ''}`}>
      <div className="clw-opt-runbar-head">
        <button
          type="button"
          className="clw-opt-runbar-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="chev">{open ? '▾' : '▸'}</span>
          {/* Never "Run again". This panel now sits next to a "New session"
              control, and two vague re-do labels a centimetre apart left users
              unable to tell which one re-analysed the campaign and which one
              cleared the conversation. Say what is being made. */}
          <span>{fullAnalysis ? 'Run Full Analysis' : hasRun ? 'New optimizer run' : 'Run optimizer'}</span>
          {!open && summary && <em className="clw-opt-runbar-sum">{summary}</em>}
        </button>
        {running && <span className="clw-opt-runbar-status">running…</span>}
      </div>

      {open && (
        <div className="clw-opt-runbar-body">
          <OptimizeWindowFields
            destination={destination}
            value={opts}
            onChange={setOpts}
            className="opt-window-clw"
          />
          <button
            type="button"
            className="clw-bulk-confirm clw-opt-runbar-go"
            onClick={fire}
            disabled={disabled || running}
            title={disabled ? (disabledReason || 'Not available') : undefined}
          >
            {running ? 'Running…' : fullAnalysis ? 'Run Full Analysis' : hasRun ? 'Start new run' : 'Run optimizer'}
          </button>
          {/* Deliberately does NOT restate a disabled reason: the gate below
              this owns that message, and saying it twice a centimetre apart
              reads as a rendering bug. The reason rides the button's title. */}
          {!disabled && (
            <div className="clw-opt-runbar-note">
              {fullAnalysis ? 'Diagnosis, performance, and budget pacing. ' : ''}
              Takes a few minutes{hasRun ? ' — replaces the reports and suggestions below' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OptimizationRunBar;
